import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDropzone } from 'react-dropzone';
import {
  Upload, FileText, File, Trash2, Download, Eye, EyeOff, Edit3,
  X, Loader2, Image, FileCode, FileSpreadsheet, Check, Link2,
  Pencil, MoreVertical, ExternalLink, ChevronDown, ChevronRight
} from 'lucide-react';
import * as contextFileService from '../services/contextFileService';
import type { ContextFile } from '../services/contextFileService';

interface ContextFilesTabProps {
  sessionId: string;
  isPublicView?: boolean;
  shareToken?: string;
  sharePermissions?: any;
  isDarkMode?: boolean;
}

function getFileIcon(mimeType: string) {
  if (mimeType === 'application/pdf') return <FileText size={20} className="text-red-500" />;
  if (mimeType === 'text/markdown') return <FileCode size={20} className="text-[#232E3F] dark:text-[#FFCF74]" />;
  if (mimeType === 'text/plain') return <FileText size={20} className="text-gray-500 dark:text-gray-400" />;
  if (mimeType === 'text/csv') return <FileSpreadsheet size={20} className="text-[#B57700]" />;
  if (mimeType === 'application/json') return <FileCode size={20} className="text-yellow-500" />;
  if (mimeType === 'text/xml') return <FileCode size={20} className="text-orange-500" />;
  if (mimeType.startsWith('image/')) return <Image size={20} className="text-[#FFB52B]" />;
  return <File size={20} className="text-gray-400" />;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function timeAgo(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return date.toLocaleDateString();
}

function getFileTypeLabel(mimeType: string): string {
  if (mimeType === 'application/pdf') return 'PDF';
  if (mimeType === 'text/markdown') return 'Markdown';
  if (mimeType === 'text/plain') return 'Text';
  if (mimeType === 'text/csv') return 'CSV';
  if (mimeType === 'application/json') return 'JSON';
  if (mimeType === 'text/xml') return 'XML';
  if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') return 'JPEG';
  if (mimeType === 'image/png') return 'PNG';
  if (mimeType === 'image/webp') return 'WebP';
  if (mimeType === 'image/gif') return 'GIF';
  return 'File';
}

// Stop keyboard events from reaching global handlers (spacebar play/pause, etc.)
const stopKeyCapture = (e: React.KeyboardEvent) => e.stopPropagation();

const TEXT_MIME_TYPES = ['text/plain', 'text/markdown', 'text/csv', 'application/json', 'text/xml'];
const IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

const ContextFilesTab: React.FC<ContextFilesTabProps> = ({
  sessionId,
  isPublicView = false,
  shareToken,
  sharePermissions,
  isDarkMode = false
}) => {
  const [files, setFiles] = useState<ContextFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewerFile, setViewerFile] = useState<ContextFile | null>(null);
  const [viewerContent, setViewerContent] = useState<string | null>(null);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [editingFile, setEditingFile] = useState<ContextFile | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editDescFile, setEditDescFile] = useState<ContextFile | null>(null);
  const [editDescText, setEditDescText] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  const isActiveRef = useRef(true);
  const menuRef = useRef<HTMLDivElement>(null);

  const token = undefined;

  const toggleExpanded = (fileId: string) => {
    setExpandedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const expandAll = () => setExpandedFiles(new Set(files.map(f => f.id)));
  const collapseAll = () => setExpandedFiles(new Set());

  const refreshFiles = useCallback(async () => {
    try {
      let result: ContextFile[];
      if (isPublicView && shareToken) {
        result = await contextFileService.listPublic(shareToken);
      } else {
        result = await contextFileService.list(sessionId, token);
      }
      setFiles(result);
      setError(null);
      // Auto-expand files on first load
      if (result.length <= 5) {
        setExpandedFiles(prev => prev.size === 0 ? new Set(result.map(f => f.id)) : prev);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId, isPublicView, shareToken, token]);

  useEffect(() => {
    refreshFiles();
  }, [refreshFiles]);

  // Auto-refresh: 5s if any pending descriptions, 15s otherwise
  useEffect(() => {
    isActiveRef.current = true;
    const hasPending = files.some(
      f => f.description_status === 'pending' || f.description_status === 'generating'
    );
    const interval = setInterval(() => {
      if (isActiveRef.current) refreshFiles();
    }, hasPending ? 5000 : 15000);
    return () => {
      isActiveRef.current = false;
      clearInterval(interval);
    };
  }, [files, refreshFiles]);

  // Close menu on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setActiveMenu(null);
      }
    };
    if (activeMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [activeMenu]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      await contextFileService.upload(sessionId, acceptedFiles, token);
      await refreshFiles();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }, [sessionId, token, refreshFiles]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
      'text/csv': ['.csv'],
      'application/json': ['.json'],
      'text/xml': ['.xml'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/gif': ['.gif']
    },
    maxSize: 50 * 1024 * 1024,
    disabled: isPublicView || uploading
  });

  const handleView = async (file: ContextFile) => {
    setViewerFile(file);
    setViewerLoading(true);
    setViewerContent(null);
    setViewerUrl(null);
    setActiveMenu(null);
    try {
      const isTextFile = TEXT_MIME_TYPES.includes(file.mime_type);
      if (isTextFile) {
        if (isPublicView && shareToken) {
          const url = await contextFileService.getPublicDownloadUrl(shareToken, file.id);
          const res = await fetch(url);
          setViewerContent(await res.text());
        } else {
          const content = await contextFileService.getContent(sessionId, file.id, token);
          setViewerContent(content);
        }
      } else {
        if (isPublicView && shareToken) {
          const url = await contextFileService.getPublicDownloadUrl(shareToken, file.id);
          setViewerUrl(url);
        } else {
          const data = await contextFileService.getWithUrl(sessionId, file.id, token);
          setViewerUrl(data.downloadUrl || null);
        }
      }
    } catch (err: any) {
      setError(err.message);
      setViewerFile(null);
    } finally {
      setViewerLoading(false);
    }
  };

  const handleDownload = async (file: ContextFile) => {
    setActiveMenu(null);
    try {
      let url: string;
      if (isPublicView && shareToken) {
        url = await contextFileService.getPublicDownloadUrl(shareToken, file.id);
      } else {
        const data = await contextFileService.getWithUrl(sessionId, file.id, token);
        url = data.downloadUrl || '';
      }
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename || file.original_filename;
      a.click();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCopyLink = async (file: ContextFile) => {
    setActiveMenu(null);
    try {
      let url: string;
      if (isPublicView && shareToken) {
        url = await contextFileService.getPublicDownloadUrl(shareToken, file.id);
      } else {
        const data = await contextFileService.getWithUrl(sessionId, file.id, token);
        url = data.downloadUrl || '';
      }
      await navigator.clipboard.writeText(url);
      setCopiedLink(file.id);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = async (file: ContextFile) => {
    setActiveMenu(null);
    setEditingFile(file);
    setEditSaving(false);
    try {
      const content = await contextFileService.getContent(sessionId, file.id, token);
      setEditContent(content);
    } catch (err: any) {
      setError(err.message);
      setEditingFile(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingFile) return;
    setEditSaving(true);
    try {
      await contextFileService.updateContent(sessionId, editingFile.id, editContent, token);
      setEditingFile(null);
      refreshFiles();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (fileId: string) => {
    try {
      await contextFileService.deleteFile(sessionId, fileId, token);
      setDeleteConfirm(null);
      setActiveMenu(null);
      refreshFiles();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleVisibility = async (file: ContextFile) => {
    setActiveMenu(null);
    try {
      await contextFileService.updateMetadata(sessionId, file.id, {
        hiddenFromShare: !file.hidden_from_share
      }, token);
      refreshFiles();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSaveDescription = async () => {
    if (!editDescFile) return;
    try {
      await contextFileService.updateMetadata(sessionId, editDescFile.id, { description: editDescText }, token);
      setEditDescFile(null);
      refreshFiles();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const isText = (mimeType: string) => TEXT_MIME_TYPES.includes(mimeType);
  const isImage = (mimeType: string) => IMAGE_MIME_TYPES.includes(mimeType);

  const hasDescription = (file: ContextFile) => Boolean(file.description);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin mr-2" size={20} />
        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Loading context files...</span>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Context Files
          </h3>
          <p className={`hidden text-sm mt-0.5 md:block ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Reference documents, papers, and images for this episode
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Expand/Collapse all */}
          {files.length > 1 && files.some(f => hasDescription(f)) && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={expandAll}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}
              >
                Expand all
              </button>
              <button
                onClick={collapseAll}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                }`}
              >
                Collapse all
              </button>
            </div>
          )}
          {!isPublicView && (
            <span className={`text-sm tabular-nums ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {files.length} file{files.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-4 py-3 text-sm text-red-700 dark:text-red-300 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-3 hover:opacity-70">
            <X size={14} />
          </button>
        </div>
      )}

      {/* Upload drop zone (authenticated only) */}
      {!isPublicView && (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
            isDragActive
              ? 'border-[#FFB52B] bg-[#FFF8EA] dark:bg-[#232E3F]/60 scale-[1.01]'
              : isDarkMode
                ? 'border-gray-700 hover:border-gray-500 bg-gray-800/30'
                : 'border-gray-200 hover:border-gray-400 bg-gray-50/50'
          } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="animate-spin" size={20} />
              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Uploading...</span>
            </div>
          ) : isDragActive ? (
            <div>
              <Upload className="mx-auto mb-2 text-[#FFB52B]" size={28} />
              <p className="font-medium text-[#B57700] dark:text-[#FFE3A1]">Drop files here...</p>
            </div>
          ) : (
            <div>
              <Upload className={`mx-auto mb-2 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} size={28} />
              <p className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Drag files here or click to browse
              </p>
              <p className={`text-xs mt-1.5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                PDF, Markdown, Text, CSV, JSON, XML, JPEG, PNG, WebP, GIF — max 50MB
              </p>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {files.length === 0 && !loading && (
        <div className={`text-center py-12 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          <File className="mx-auto mb-3 opacity-40" size={40} />
          <p className="text-sm">No context files uploaded yet</p>
          {!isPublicView && (
            <p className="text-xs mt-1 opacity-70">Upload reference papers, notes, or images to provide context</p>
          )}
        </div>
      )}

      {/* File list */}
      <div className="space-y-2">
        {files.map(file => {
          const isExpanded = expandedFiles.has(file.id);
          const hasDesc = hasDescription(file);

          return (
            <div
              key={file.id}
              className={`rounded-xl border transition-colors ${
                isDarkMode
                  ? 'bg-gray-800/60 border-gray-700/80 hover:border-gray-600'
                  : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'
              }`}
            >
              {/* File header row */}
              <div className="flex items-center gap-3 px-4 py-3">
                {/* Expand/collapse chevron */}
                {hasDesc ? (
                  <button
                    onClick={() => toggleExpanded(file.id)}
                    className={`flex-shrink-0 p-0.5 rounded transition-colors ${
                      isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                    }`}
                  >
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                ) : (
                  <div className="w-5 flex-shrink-0" />
                )}

                {/* Icon */}
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                  isDarkMode ? 'bg-gray-700/60' : 'bg-gray-100'
                }`}>
                  {getFileIcon(file.mime_type)}
                </div>

                {/* File info — clickable to expand */}
                <div
                  className="min-w-0 flex-1 cursor-pointer"
                  onClick={() => hasDesc && toggleExpanded(file.id)}
                >
                  <p className={`font-medium text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {file.filename}
                  </p>
                  <div className={`flex items-center gap-1.5 text-xs mt-0.5 flex-wrap ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wide ${
                      isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {getFileTypeLabel(file.mime_type)}
                    </span>
                    <span>{formatFileSize(file.file_size)}</span>
                    {file.uploader_email && !isPublicView && (
                      <>
                        <span className="opacity-50">·</span>
                        <span>{file.uploader_email}</span>
                      </>
                    )}
                    {file.created_at && (
                      <>
                        <span className="opacity-50">·</span>
                        <span>{timeAgo(file.created_at)}</span>
                      </>
                    )}
                    {file.hidden_from_share && !isPublicView && (
                      <>
                        <span className="opacity-50">·</span>
                        <span className="text-yellow-500 dark:text-yellow-400 flex items-center gap-0.5">
                          <EyeOff size={10} /> Hidden
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Quick actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleView(file)}
                    title="View file"
                    className={`p-2 rounded-lg transition-colors ${
                      isDarkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => handleDownload(file)}
                    title="Download file"
                    className={`p-2 rounded-lg transition-colors ${
                      isDarkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Download size={16} />
                  </button>
                  <button
                    onClick={() => handleCopyLink(file)}
                    title="Copy download link"
                    className={`p-2 rounded-lg transition-colors ${
                      copiedLink === file.id
                        ? 'text-[#B57700] dark:text-[#FFCF74]'
                        : isDarkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {copiedLink === file.id ? <Check size={16} /> : <Link2 size={16} />}
                  </button>

                  {/* More menu (authenticated only) */}
                  {!isPublicView && (
                    <div className="relative" ref={activeMenu === file.id ? menuRef : undefined}>
                      <button
                        onClick={() => setActiveMenu(activeMenu === file.id ? null : file.id)}
                        className={`p-2 rounded-lg transition-colors ${
                          isDarkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <MoreVertical size={16} />
                      </button>
                      {activeMenu === file.id && (
                        <div className={`absolute right-0 top-full mt-1 z-20 w-52 rounded-lg border shadow-lg py-1 ${
                          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                        }`}>
                          {isText(file.mime_type) && (
                            <button
                              onClick={() => handleEdit(file)}
                              className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-left transition-colors ${
                                isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                              }`}
                            >
                              <Edit3 size={14} className="opacity-60" />
                              Edit content
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setEditDescFile(file);
                              setEditDescText(file.description || '');
                              setActiveMenu(null);
                            }}
                            className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-left transition-colors ${
                              isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            <Pencil size={14} className="opacity-60" />
                            Edit description
                          </button>
                          <button
                            onClick={() => handleToggleVisibility(file)}
                            className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-left transition-colors ${
                              isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-50 text-gray-700'
                            }`}
                          >
                            {file.hidden_from_share ? (
                              <><Eye size={14} className="opacity-60" /> Show in shares</>
                            ) : (
                              <><EyeOff size={14} className="opacity-60" /> Hide from shares</>
                            )}
                          </button>
                          <div className={`my-1 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`} />
                          {deleteConfirm === file.id ? (
                            <div className="px-3.5 py-2 space-y-2">
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Delete this file permanently?
                              </p>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleDelete(file.id)}
                                  className="flex-1 px-2 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 font-medium"
                                >
                                  Delete
                                </button>
                                <button
                                  onClick={() => setDeleteConfirm(null)}
                                  className={`flex-1 px-2 py-1.5 text-xs rounded-md font-medium ${
                                    isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                                  }`}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirm(file.id)}
                              className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-sm text-left transition-colors text-red-500 ${
                                isDarkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'
                              }`}
                            >
                              <Trash2 size={14} className="opacity-60" />
                              Delete file
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Collapsible description area */}
              {isExpanded && hasDesc && (
                <div className={`px-4 pb-3 ml-[28px] border-t ${
                  isDarkMode ? 'border-gray-700/50' : 'border-gray-100'
                }`}>
                  <div className="pt-3">
                    {file.description ? (
                      <div className={`text-sm prose prose-sm max-w-none leading-relaxed ${
                        isDarkMode
                          ? 'prose-invert text-gray-300 prose-a:text-[#FFCF74] prose-strong:text-gray-200'
                          : 'text-gray-600 prose-a:text-[#B57700] prose-strong:text-gray-800'
                      }`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{file.description}</ReactMarkdown>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ===== Viewer Modal ===== */}
      {viewerFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setViewerFile(null)}
          onKeyDown={stopKeyCapture}
        >
          <div
            className={`relative flex flex-col rounded-xl shadow-2xl overflow-hidden ${
              isDarkMode ? 'bg-gray-900' : 'bg-white'
            } ${
              viewerFile.mime_type === 'application/pdf' || isImage(viewerFile.mime_type)
                ? 'w-[95vw] h-[95vh] max-w-[1600px]'
                : 'w-full max-w-4xl max-h-[90vh] mx-4'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-5 py-3 border-b flex-shrink-0 ${
              isDarkMode ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center gap-2.5 min-w-0">
                {getFileIcon(viewerFile.mime_type)}
                <div className="min-w-0">
                  <h4 className={`font-medium text-sm truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {viewerFile.filename}
                  </h4>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {getFileTypeLabel(viewerFile.mime_type)} · {formatFileSize(viewerFile.file_size)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => handleDownload(viewerFile)}
                  title="Download"
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                  }`}
                >
                  <Download size={16} />
                </button>
                <button
                  onClick={() => handleCopyLink(viewerFile)}
                  title="Copy download link"
                  className={`p-2 rounded-lg transition-colors ${
                    copiedLink === viewerFile.id
                      ? 'text-[#B57700] dark:text-[#FFCF74]'
                      : isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                  }`}
                >
                  {copiedLink === viewerFile.id ? <Check size={16} /> : <Link2 size={16} />}
                </button>
                {viewerUrl && (
                  <a
                    href={viewerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open in new tab"
                    className={`p-2 rounded-lg transition-colors ${
                      isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                    }`}
                  >
                    <ExternalLink size={16} />
                  </a>
                )}
                <button
                  onClick={() => setViewerFile(null)}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'
                  }`}
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {viewerLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="animate-spin mr-2" size={24} />
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Loading...</span>
                </div>
              ) : viewerContent !== null ? (
                <div className="p-6">
                  {viewerFile.mime_type === 'text/markdown' ? (
                    <div className={`prose prose-sm md:prose-base max-w-none ${isDarkMode ? 'prose-invert' : ''}`}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{viewerContent}</ReactMarkdown>
                    </div>
                  ) : viewerFile.mime_type === 'application/json' ? (
                    <pre className={`text-sm font-mono whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                      {(() => { try { return JSON.stringify(JSON.parse(viewerContent), null, 2); } catch { return viewerContent; } })()}
                    </pre>
                  ) : (
                    <pre className={`text-sm font-mono whitespace-pre-wrap leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}>
                      {viewerContent}
                    </pre>
                  )}
                </div>
              ) : viewerUrl ? (
                viewerFile.mime_type === 'application/pdf' ? (
                  <iframe src={viewerUrl} className="w-full h-full border-0" title={viewerFile.filename} />
                ) : isImage(viewerFile.mime_type) ? (
                  <div className="flex items-center justify-center p-6 h-full">
                    <img src={viewerUrl} alt={viewerFile.filename} className="max-w-full max-h-full object-contain rounded-lg" />
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <File className={`mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-gray-300'}`} size={48} />
                    <a href={viewerUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 font-medium text-[#B57700] hover:text-[#8B5B00] dark:text-[#FFCF74] dark:hover:text-[#FFE3A1]">
                      <ExternalLink size={16} /> Open file in new tab
                    </a>
                  </div>
                )
              ) : (
                <div className="text-center py-16">
                  <p className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>No content available.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== Content Editor Modal ===== */}
      {editingFile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setEditingFile(null)}
          onKeyDown={stopKeyCapture}
        >
          <div
            className={`relative w-[95vw] h-[95vh] max-w-[1400px] rounded-xl shadow-2xl overflow-hidden flex flex-col ${
              isDarkMode ? 'bg-gray-900' : 'bg-white'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className={`flex items-center justify-between px-5 py-3 border-b flex-shrink-0 ${
              isDarkMode ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center gap-2.5">
                {getFileIcon(editingFile.mime_type)}
                <div>
                  <h4 className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Editing: {editingFile.filename}
                  </h4>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {getFileTypeLabel(editingFile.mime_type)} · {formatFileSize(editingFile.file_size)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSaveEdit}
                  disabled={editSaving}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#FFB52B] px-4 py-1.5 text-sm font-medium text-[#232E3F] transition-colors hover:bg-[#FFC54D] disabled:opacity-50"
                >
                  {editSaving && <Loader2 className="animate-spin" size={14} />}
                  Save changes
                </button>
                <button
                  onClick={() => setEditingFile(null)}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'
                  }`}
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              onKeyDown={stopKeyCapture}
              className={`flex-1 w-full p-5 font-mono text-sm leading-relaxed resize-none outline-none ${
                isDarkMode ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800'
              }`}
              spellCheck={false}
              autoFocus
            />
          </div>
        </div>
      )}

      {/* ===== Description Editor Modal ===== */}
      {editDescFile && (
        <div
          className="fixed inset-0 z-50 flex items-stretch bg-black/70 backdrop-blur-sm"
          onClick={() => setEditDescFile(null)}
          onKeyDown={stopKeyCapture}
        >
          <div
            className={`relative w-full h-full flex flex-col ${
              isDarkMode ? 'bg-gray-900' : 'bg-white'
            }`}
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-center justify-between px-5 py-3 border-b flex-shrink-0 ${
              isDarkMode ? 'border-gray-700 bg-gray-800/80' : 'border-gray-200 bg-gray-50'
            }`}>
              <div className="flex items-center gap-2.5">
                <Pencil size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                <div>
                  <h4 className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    Edit Description — {editDescFile.filename}
                  </h4>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Markdown formatting supported
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSaveDescription}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#FFB52B] px-4 py-1.5 text-sm font-medium text-[#232E3F] transition-colors hover:bg-[#FFC54D]"
                >
                  Save
                </button>
                <button
                  onClick={() => setEditDescFile(null)}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-200 text-gray-600'
                  }`}
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Side-by-side editor + preview */}
            <div className="flex-1 flex min-h-0">
              {/* Editor pane */}
              <div className={`flex-1 flex flex-col border-r ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className={`px-4 py-2 text-xs font-medium flex-shrink-0 ${
                  isDarkMode ? 'text-gray-400 bg-gray-800/50' : 'text-gray-500 bg-gray-50'
                }`}>
                  Markdown Source
                </div>
                <textarea
                  value={editDescText}
                  onChange={e => setEditDescText(e.target.value)}
                  onKeyDown={stopKeyCapture}
                  className={`flex-1 w-full p-4 font-mono text-sm leading-relaxed resize-none outline-none ${
                    isDarkMode ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800'
                  }`}
                  placeholder="Describe this file and its relevance to the episode..."
                  spellCheck={false}
                  autoFocus
                />
              </div>

              {/* Preview pane */}
              <div className="flex-1 flex flex-col">
                <div className={`px-4 py-2 text-xs font-medium flex-shrink-0 ${
                  isDarkMode ? 'text-gray-400 bg-gray-800/50' : 'text-gray-500 bg-gray-50'
                }`}>
                  Preview
                </div>
                <div className={`flex-1 overflow-auto p-4 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
                  {editDescText ? (
                    <div className={`text-sm prose prose-sm max-w-none leading-relaxed ${
                      isDarkMode
                        ? 'prose-invert text-gray-300 prose-a:text-[#FFCF74] prose-strong:text-gray-200'
                        : 'text-gray-600 prose-a:text-[#B57700] prose-strong:text-gray-800'
                    }`}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{editDescText}</ReactMarkdown>
                    </div>
                  ) : (
                    <p className={`text-sm italic ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                      Start typing to see a preview...
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContextFilesTab;
